import { useState } from 'react'
import { Link } from 'react-router-dom'
import { resetPasswordForEmail } from '@/lib/services/auth'
import { routes } from '@/lib/config'
import { AuthLayout } from '@/components/auth'
import { Button, Alert } from '@/components/ui'
import { EmailIcon } from '@/components/ui/Icons'
import { useTranslation } from '@/contexts/TranslationContext'

const INPUT_BASE_CLASS =
  'block w-full rounded-lg border-0 bg-white py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-green-600 sm:text-sm pl-10 pr-3'

export default function ForgotPassword() {
  const { t } = useTranslation()
  const [email, setEmail]       = useState('')
  const [isLoading, setLoading] = useState(false)
  const [sent, setSent]         = useState(false)
  const [error, setError]       = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { error: err } = await resetPasswordForEmail(email)
      if (err) throw err
      setSent(true)
    } catch {
      // Always show the success message to avoid leaking whether an email is registered
      setSent(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout subtitle={t('auth.forgotPasswordSubtitle')}>
      {sent ? (
        <div className="space-y-6">
          <Alert variant="success">{t('auth.resetEmailSent')}</Alert>
          <Link
            to={routes.login}
            className="block text-center text-sm font-semibold text-green-600 hover:text-green-700"
          >
            {t('auth.backToLogin')}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
              {t('auth.email')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <EmailIcon />
              </div>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                className={INPUT_BASE_CLASS}
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <Button type="submit" fullWidth loading={isLoading}>
            {t('auth.sendResetEmail')}
          </Button>

          <p className="text-center text-sm">
            <Link
              to={routes.login}
              className="font-semibold text-green-600 hover:text-green-700"
            >
              {t('auth.backToLogin')}
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  )
}
